import { Static, Type } from '@sinclair/typebox';

// =====================================================================
// Inline AI assist — contrats partagés du bouton IA contextuel.
//
// Le bouton IA inline et Pulse (l'agent conversationnel) sont deux surfaces
// d'une même IA : cf. docs/inline-ai-assist-plan.md. Ce fichier porte le
// contrat de l'action de champ single-shot (générer / reformuler / corriger…)
// et la métadonnée `meta.ai` posée sur les schémas TypeBox éligibles.
// =====================================================================

/** Actions IA disponibles sur un champ. Jeu volontairement petit et déterministe. */
export enum AiAction {
  GENERATE = 'generate',
  REPHRASE = 'rephrase',
  FIX = 'fix',
  SHORTEN = 'shorten',
  EXPAND = 'expand',
  TONE = 'tone',
  TRANSLATE = 'translate',
  /**
   * Demande libre : texte saisi dans la boîte sans verbe d'action (bouton Envoyer).
   * N'est jamais un chip ni un défaut — uniquement portée par `user_instruction`.
   */
  FREEFORM = 'freeform',
}

/**
 * Type sémantique d'un champ. Choisit le template de prompt côté backend et le
 * jeu d'actions par défaut côté frontend. C'est la seule info réellement
 * nouvelle à poser dans `meta.ai` — le reste (libellé, longueur max, balises
 * autorisées) est déjà porté par le schéma TypeBox.
 */
export enum AiFieldKind {
  SERVICE_DESCRIPTION = 'service_description',
  CATEGORY_DESCRIPTION = 'category_description',
  BUSINESS_BIO = 'business_bio',
  PERSON_BIO = 'person_bio',
  ARTICLE_BODY = 'article_body',
  SUMMARY = 'summary',
  EMAIL_SUBJECT = 'email_subject',
  EMAIL_BODY = 'email_body',
  SMS_BODY = 'sms_body',
  MARKETING_COPY = 'marketing_copy',
  WEB_TEXT_BLOCK = 'web_text_block',
  SEO_META = 'seo_meta',
  HEADLINE = 'headline',
  FAQ_ITEM = 'faq_item',
  TESTIMONIAL = 'testimonial',
  FEATURE_ITEM = 'feature_item',
  SHORT_DESCRIPTION = 'short_description',
  PERSONAL_MESSAGE = 'personal_message',
  /** Nom court et accrocheur d'une prestation de service. */
  SERVICE_NAME = 'service_name',
  /** Titre SEO d'un article de blog. */
  ARTICLE_TITLE = 'article_title',
}

/** Ton cible pour l'action `tone`. */
export enum AiTone {
  PROFESSIONAL = 'professional',
  WARM = 'warm',
  PUNCHY = 'punchy',
}

/**
 * Voix narrative du contenu généré — orthogonale au ton. « Je » pour un
 * professionnel solo, « Nous » pour une équipe. Choisie à chaque action.
 */
export enum AiVoice {
  SINGULAR = 'je',
  PLURAL = 'nous',
}

/**
 * Shape du bloc `meta.ai` posé sur un schéma TypeBox éligible au bouton IA.
 * Opt-in : seuls les champs annotés affichent le bouton. `actions` est
 * optionnel — à défaut, le frontend dérive le menu de `AI_FIELD_DEFAULT_ACTIONS`.
 */
export interface AiFieldMeta {
  kind: AiFieldKind;
  actions?: AiAction[];
}

/**
 * Jeu d'actions par défaut par type de champ — utilisé par le frontend quand
 * `meta.ai.actions` n'est pas précisé. `translate` est volontairement exclu
 * des défauts (activation explicite seulement).
 */
export const AI_FIELD_DEFAULT_ACTIONS: Record<AiFieldKind, AiAction[]> = {
  [AiFieldKind.SERVICE_DESCRIPTION]: [
    AiAction.GENERATE,
    AiAction.REPHRASE,
    AiAction.FIX,
    AiAction.SHORTEN,
    AiAction.EXPAND,
    AiAction.TONE,
  ],
  [AiFieldKind.CATEGORY_DESCRIPTION]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
  [AiFieldKind.BUSINESS_BIO]: [
    AiAction.GENERATE,
    AiAction.REPHRASE,
    AiAction.FIX,
    AiAction.SHORTEN,
    AiAction.EXPAND,
    AiAction.TONE,
  ],
  [AiFieldKind.PERSON_BIO]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN, AiAction.TONE],
  [AiFieldKind.ARTICLE_BODY]: [
    AiAction.GENERATE,
    AiAction.REPHRASE,
    AiAction.FIX,
    AiAction.SHORTEN,
    AiAction.EXPAND,
    AiAction.TONE,
  ],
  [AiFieldKind.SUMMARY]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
  [AiFieldKind.EMAIL_SUBJECT]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.SHORTEN, AiAction.TONE],
  [AiFieldKind.EMAIL_BODY]: [
    AiAction.GENERATE,
    AiAction.REPHRASE,
    AiAction.FIX,
    AiAction.SHORTEN,
    AiAction.EXPAND,
    AiAction.TONE,
  ],
  [AiFieldKind.SMS_BODY]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
  [AiFieldKind.MARKETING_COPY]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.SHORTEN, AiAction.TONE],
  [AiFieldKind.WEB_TEXT_BLOCK]: [
    AiAction.GENERATE,
    AiAction.REPHRASE,
    AiAction.FIX,
    AiAction.SHORTEN,
    AiAction.EXPAND,
    AiAction.TONE,
  ],
  [AiFieldKind.SEO_META]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.SHORTEN],
  [AiFieldKind.HEADLINE]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.SHORTEN, AiAction.TONE],
  [AiFieldKind.FAQ_ITEM]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.EXPAND],
  [AiFieldKind.TESTIMONIAL]: [AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
  [AiFieldKind.FEATURE_ITEM]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
  [AiFieldKind.SHORT_DESCRIPTION]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
  [AiFieldKind.PERSONAL_MESSAGE]: [AiAction.REPHRASE, AiAction.FIX],
  [AiFieldKind.SERVICE_NAME]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
  [AiFieldKind.ARTICLE_TITLE]: [AiAction.GENERATE, AiAction.REPHRASE, AiAction.FIX, AiAction.SHORTEN],
};

const enumLiteralUnion = (e: Record<string, string>): ReturnType<typeof Type.Union> =>
  Type.Union(Object.values(e).map((v): ReturnType<typeof Type.Literal> => Type.Literal(v)));

// =====================================================================
// Field action — contrat de l'endpoint POST /app/ai-assist/field-action
// =====================================================================

/** Contraintes du champ, dérivées de son schéma TypeBox (longueur, balises rich-text). */
export const FieldActionConstraintsSchema = Type.Object({
  max_length: Type.Optional(Type.Number({ minimum: 1 })),
  /** Balises HTML autorisées pour un champ rich_text (cf. `meta.allowed_tags`). */
  allowed_tags: Type.Optional(Type.Array(Type.String({ maxLength: 20 }), { maxItems: 30 })),
});

/**
 * Un message du fil local d'un champ (copilote inline conversationnel).
 * `user` = demande de l'utilisateur (chip d'action ou texte libre) ;
 * `assistant` = proposition précédente de Pulse. Le thread reste éphémère côté
 * frontend ; il est renvoyé au backend pour rejouer le contexte multi-tours.
 */
export const AiChatMessageSchema = Type.Object({
  role: Type.Union([Type.Literal('user'), Type.Literal('assistant')]),
  content: Type.String({ minLength: 0, maxLength: 50000 }),
});

export type AiChatMessage = Static<typeof AiChatMessageSchema>;

export const FieldActionRequestSchema = Type.Object({
  action: enumLiteralUnion(AiAction),
  kind: enumLiteralUnion(AiFieldKind),
  /** Contenu actuel du champ. Vide est autorisé (cas `generate`). */
  current_value: Type.String({ minLength: 0, maxLength: 50000 }),
  /**
   * Historique du fil local du champ (échanges précédents), pour le re-prompt
   * multi-tours. Vide/absent = première action single-shot. Le dernier message
   * `user` correspond à la demande courante (l'action déclenchée ou le texte libre).
   */
  messages: Type.Optional(Type.Array(AiChatMessageSchema, { maxItems: 40 })),
  /** Demande libre de l'utilisateur, quand l'action n'est pas un preset (re-prompt). */
  user_instruction: Type.Optional(Type.String({ maxLength: 2000 })),
  constraints: Type.Optional(FieldActionConstraintsSchema),
  /** Tranche légère de l'entité environnante, pour contextualiser la génération. */
  entity_context: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
  /** Locale de l'utilisateur (next-intl). */
  locale: Type.Optional(Type.String({ maxLength: 10 })),
  /** Locale cible pour l'action `translate`. */
  target_locale: Type.Optional(Type.String({ maxLength: 10 })),
  /** Ton cible pour l'action `tone`. */
  tone: Type.Optional(enumLiteralUnion(AiTone)),
  /** Voix narrative (« je » / « nous ») appliquée aux actions de rédaction. */
  voice: Type.Optional(enumLiteralUnion(AiVoice)),
});

export const FieldActionResponseSchema = Type.Object({
  /** Contenu suggéré, prêt à être inséré dans le champ après validation utilisateur. */
  result: Type.String(),
  debug: Type.Optional(
    Type.Object({
      model_profile: Type.Optional(Type.String()),
      model: Type.Optional(Type.String()),
      prompt_tokens: Type.Optional(Type.Number()),
      completion_tokens: Type.Optional(Type.Number()),
      total_tokens: Type.Optional(Type.Number()),
    }),
  ),
});

export type FieldActionConstraints = Static<typeof FieldActionConstraintsSchema>;
export type FieldActionRequest = Static<typeof FieldActionRequestSchema>;
export type FieldActionResponse = Static<typeof FieldActionResponseSchema>;

/**
 * Événement SSE émis par l'endpoint streaming `POST /app/ai-assist/field-action/stream`.
 * `delta` : fragment de texte au fil de l'eau ; `done` : résultat final nettoyé ;
 * `error` : échec de génération.
 */
export type FieldActionStreamEvent =
  | { type: 'delta'; text: string }
  | { type: 'done'; result: string }
  | { type: 'error' };
